// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { IlliquidAssetEvent } from '@shared/data-access/types/engine-types';
import { SERIES_COLORS } from '@shared/ui-kit';
import { formatDate, formatToMoney, hexToRgba } from '@shared/ui-kit/utils';
import { isUndefined } from '@shared/utils';
import {
  ColorType,
  createChart,
  LineType,
  type AreaData,
  type IChartApi,
  type UTCTimestamp,
} from 'lightweight-charts';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  buildIlliquidValuationChartPoints,
  type IlliquidValuationChartPoint,
} from '../../utils/build-illiquid-valuation-chart-points';

const CHART_LINE_COLOR = SERIES_COLORS[0]!;

const CHART_MIN_HEIGHT = 200;

type TooltipData = {
  readonly date: string;
  readonly value: string;
};

export type UseIlliquidAssetValueEvolutionChartProps = {
  readonly events: readonly IlliquidAssetEvent[] | undefined;

  readonly currency: string;
};

export const useIlliquidAssetValueEvolutionChart = ({
  events,
  currency,
}: UseIlliquidAssetValueEvolutionChartProps) => {
  const { i18n } = useTranslation();

  const chartContainerRef = useRef<HTMLDivElement>(null);

  const tooltipRef = useRef<HTMLDivElement>(null);

  const chartRef = useRef<IChartApi>();

  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const [tooltipValues, setTooltipValues] = useState<TooltipData>();

  const points = useMemo(
    () => buildIlliquidValuationChartPoints(events, currency),
    [currency, events],
  );

  const pointsByTime = useMemo(() => {
    const map = new Map<UTCTimestamp, IlliquidValuationChartPoint>();

    for (const point of points) {
      map.set(point.time, point);
    }

    return map;
  }, [points]);

  const seriesData = useMemo(
    (): AreaData[] =>
      points.map(point => ({
        time: point.time,
        value: point.value,
      })),
    [points],
  );

  useEffect(() => {
    if (!chartContainerRef.current || seriesData.length === 0) return;

    chartRef.current = createChart(chartContainerRef.current, {
      layout: {
        background: { type: ColorType.Solid, color: '#edf0f9' },
        textColor: '#515F70',
        fontFamily: 'Poppins',
        fontSize: 12,
      },
      localization: {
        locale: i18n.language,
      },
      width: chartContainerRef.current.clientWidth,
      height: Math.max(chartContainerRef.current.clientHeight, CHART_MIN_HEIGHT),
      handleScroll: false,
      handleScale: false,
      grid: {
        vertLines: {
          visible: false,
        },
        horzLines: {
          visible: false,
        },
      },
      crosshair: {
        horzLine: { visible: false, labelVisible: false },
        vertLine: { labelVisible: false },
      },
      leftPriceScale: {
        visible: false,
      },
      rightPriceScale: {
        visible: false,
      },
      timeScale: {
        allowBoldLabels: false,
        borderVisible: false,
        timeVisible: false,
        tickMarkFormatter: (time: UTCTimestamp) => {
          const date = new Date(Number(time) * 1000);

          return (
            formatDate(date, 'month-year', i18n.language, {
              month: 'short',
            }) ?? ''
          );
        },
      },
    });

    const areaSeries = chartRef.current.addAreaSeries({
      lineColor: CHART_LINE_COLOR,
      topColor: hexToRgba(CHART_LINE_COLOR, 0.35),
      bottomColor: hexToRgba(CHART_LINE_COLOR, 0.05),
      lineType: LineType.Simple,
      lineWidth: 2,
      crosshairMarkerVisible: true,
      crosshairMarkerRadius: 5,
      crosshairMarkerBorderColor: '#ffffff',
      crosshairMarkerBackgroundColor: CHART_LINE_COLOR,
      priceLineVisible: false,
      lastValueVisible: false,
      priceFormat: { type: 'volume' },
    });

    areaSeries.setData(seriesData);

    chartRef.current.timeScale().fitContent();

    /* istanbul ignore next */
    chartRef.current.subscribeCrosshairMove(param => {
      if (isUndefined(param.point) || !param.time) {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }

        return;
      }

      const serie = param.seriesData.get(areaSeries) as AreaData | undefined;

      if (isUndefined(serie)) {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }

        return;
      }

      const pointMeta = pointsByTime.get(serie.time as UTCTimestamp);

      const tooltipCurrency = pointMeta?.currency ?? currency;

      const tooltipDateSource = pointMeta?.eventDate ?? String(param.time);

      setTooltipValues({
        date: formatDate(tooltipDateSource, 'medium', i18n.language) ?? '',
        value: formatToMoney(serie.value, tooltipCurrency),
      });

      if (!tooltipRef.current || !chartRef.current) return;

      const container = chartContainerRef.current!;

      const toolTipWidth = tooltipRef.current.clientWidth;

      const toolTipHeight = tooltipRef.current.clientHeight;

      const toolTipMargin = 15;

      const y = param.point.y;

      const left =
        param.point.x + toolTipMargin > container.clientWidth - toolTipWidth
          ? param.point.x - toolTipMargin - toolTipWidth
          : param.point.x + toolTipMargin;

      const top =
        y + toolTipMargin > container.clientHeight - toolTipHeight
          ? y - toolTipHeight - toolTipMargin
          : y + toolTipMargin;

      tooltipRef.current.style.left = `${left}px`;

      tooltipRef.current.style.top = `${top}px`;

      tooltipRef.current.style.display = 'block';
    });

    resizeObserverRef.current = new ResizeObserver(entries => {
      const { width, height } = entries[0]!.contentRect;

      chartRef.current?.applyOptions({
        width,
        height: Math.max(height, CHART_MIN_HEIGHT),
      });

      chartRef.current?.timeScale().fitContent();
    });

    resizeObserverRef.current.observe(chartContainerRef.current);

    return () => {
      resizeObserverRef.current?.disconnect();

      chartRef.current?.remove();
    };
  }, [currency, i18n.language, pointsByTime, seriesData]);

  return {
    points,
    chartContainerRef,
    tooltipRef,
    tooltipValues,
  };
};
